import { useUploadFile } from '@/api/file/file.hooks';
import { useCreateCategory, useGetThemes } from '@/api/glow-up/glow-up.hooks';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CreateCategoryRequest } from '@repo/types/src/glow-up';
import { CirclePlus, Upload, X } from 'lucide-react';
import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { toast } from 'sonner';

export const AddCategory = ({ refetch }: { refetch: () => void }) => {
  const mutation = useCreateCategory();
  const uploadImageMutation = useUploadFile();
  const { themes } = useGetThemes();
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    themeId: '',
    title: '',
    description: '',
    image: '',
  });

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFormData({ ...formData, image: file.name });
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFormData({ ...formData, image: '' });
  };

  const handleUploadAndCreate = async () => {
    if (
      !formData.themeId ||
      !formData.title ||
      !formData.description ||
      !selectedFile
    ) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      // First upload the image
      toast.info("Upload de l'image en cours...");
      const uploadResult = await uploadImageMutation.mutateAsync(selectedFile);

      // Then create the category with the uploaded image URL
      const payload: CreateCategoryRequest = {
        themeId: parseInt(formData.themeId, 10),
        title: formData.title,
        description: formData.description,
        image: uploadResult.url,
      };

      mutation.mutate(payload, {
        onSuccess: (data) => {
          setOpen(false);
          setSelectedFile(null);
          setFormData({ themeId: '', title: '', description: '', image: '' });
          refetch();
          toast.success(`La catégorie ${data.title} a été créée avec succès!`, {
            duration: 3000,
          });
        },
        onError: () => {
          toast.error(
            `Erreur lors de la création de la catégorie ${payload.title}`,
            {
              duration: 3000,
            },
          );
        },
      });
    } catch {
      toast.error("Erreur lors de l'upload de l'image");
    }
  };

  const isLoading = uploadImageMutation.isPending || mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button>
          <div className="flex flex-row gap-2 items-center">
            <CirclePlus size={24} />
            <span>Ajouter une catégorie</span>
          </div>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-center">
            Ajouter une catégorie
          </DialogTitle>
          <DialogDescription className="text-center">
            Créer une nouvelle catégorie pour un thème
          </DialogDescription>
        </DialogHeader>

        <div className="w-full flex flex-col space-y-4">
          <div>
            <Select
              value={formData.themeId}
              onValueChange={(value) =>
                setFormData({ ...formData, themeId: value })
              }
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un thème" />
              </SelectTrigger>
              <SelectContent>
                {themes?.map((theme) => (
                  <SelectItem key={theme.id} value={theme.id.toString()}>
                    {theme.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Input
              type="text"
              placeholder="Titre de la catégorie"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              disabled={isLoading}
            />
          </div>

          <div>
            <Input
              type="text"
              placeholder="Description de la catégorie"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Image</label>
            {!selectedFile ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="mt-2">
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <span className="text-blue-600 hover:text-blue-500">
                      Cliquez pour sélectionner
                    </span>
                    <span className="text-gray-500"> ou glissez une image</span>
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileSelect}
                    disabled={isLoading}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  PNG, JPG, WebP jusqu'à 10MB
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 p-2 rounded">
                    <Upload className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveFile}
                  disabled={isLoading}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <DialogClose asChild>
            <Button variant="ghost" disabled={isLoading}>
              Annuler
            </Button>
          </DialogClose>
          <Button
            variant="default"
            onClick={handleUploadAndCreate}
            disabled={isLoading}
          >
            {isLoading ? 'Création...' : 'Ajouter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
